import 'react-big-calendar/lib/css/react-big-calendar.css';

import { compareAsc } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import { groupBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';

import { TimeSlotProps } from '@common/util/getTimeSlots';

const locales = {
  'en-GB': enGB,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

function Event({ event }: any) {
  return (
    <strong
      style={{
        fontSize: 13,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
      }}>
      {event.title}
    </strong>
  );
}

interface Props {
  onChange: (data: any) => void;
  defaultValue?: any | null | undefined;
  minimumSlots: number;
}

const getNextWeekday = () => {
  const date = new Date();
  while (date.getDay() === 6 || date.getDay() === 0) {
    date.setDate(date.getDate() + 1);
  }
  return date;
};

// The minimum hours after which you can book a job.
const MIN_HOURS = 4;

const CalendarComponent = (props: Props) => {
  const { defaultValue } = props;
  let disabled = !!defaultValue;
  const data = getEvents(defaultValue);
  const [eventList, setEventList] = useState(data);

  let tomorrow = getNextWeekday();
  if (defaultValue) {
    // if there is a default values then select the min date out of the default values
    tomorrow = getMinDate(defaultValue);
  } else {
    // otherwise check if it is already past 3pm after adding MIN HOURS
    // if it is then set the date to the next day
    tomorrow = utcToZonedTime(tomorrow, 'Europe/London');

    tomorrow.setHours(tomorrow.getHours() + MIN_HOURS);
    if (tomorrow.getHours() >= 15) {
      tomorrow.setDate(tomorrow.getDate() + 1);
    }
  }

  useEffect(() => {
    if (defaultValue) {
      const selectedEvents = data.filter((event) => event.selected);
      if (selectedEvents && selectedEvents.length > 0) {
        props.onChange(selectedEvents);
      }
    }
  }, [defaultValue, data]);

  const { defaultDate, components, views, eventPropGetter, min, max, formats } =
    useMemo(
      () => ({
        defaultDate: tomorrow,
        components: {
          event: Event,
        },
        formats: {
          eventTimeRangeFormat: (_: any, culture: any) => culture,
          agendaHeaderFormat: ({ start, end }: any, culture: any, local: any) =>
            `${local.format(start, 'P', culture)} – ${local.format(
              end,
              'P',
              culture,
            )}`,
        },
        views: [Views.DAY, Views.WEEK],
        min: new Date(1972, 0, 1, 8, 0, 0),
        max: new Date(1972, 0, 1, 18, 0, 0),
        // dayLayoutAlgorithm: 'no-overlap',

        eventPropGetter: (event: any) => ({
          style: {
            backgroundColor: event.selected ? 'green' : 'lightgray',
            color: event.selected ? 'white' : 'black',
          },
        }),
      }),
      [],
    );

  const handleSelectEvent = useCallback(
    (selectedEvent: any) => {
      // if the calendar is not disabled only then allow the user to select an event
      if (!disabled) {
        const updatedEventList = eventList.map((event) => {
          if (event.id === selectedEvent.id) {
            event.selected = !event.selected;
          }
          return event;
        });
        const selectedEvents = eventList.filter((event) => event.selected);

        if (selectedEvents && selectedEvents.length > 0) {
          props.onChange(selectedEvents);
        } else {
          props.onChange(undefined);
        }
        setEventList(updatedEventList);
      }
    },
    [disabled],
  );

  const showSelectedEvents = () => {
    const selectedEvents = eventList.filter((event) => event.selected);
    let result: any = undefined;
    if (selectedEvents && selectedEvents.length > 0) {
      result = groupBy(selectedEvents, 'date');
    }
    return printSelectedEvents(result);
  };

  const printSelectedEvents = (result: any) => {
    if (result) {
      const print = Object.entries(result).map((entry, index) => {
        const date = new Date(entry[0]);
        const timeSlotList = (entry[1] as [])?.map((timeSlot: any) => {
          return ' From ' + timeSlot?.title;
        });
        return (
          <div style={{ marginTop: 20 }} key={date.toDateString() + index}>
            <ul style={{ fontSize: 16, fontWeight: 600 }}>
              {format(date, 'EEEE, MMMM do, yyyy')}
            </ul>
            <div style={{ marginLeft: 25, marginTop: 5 }}>
              {timeSlotList.map((ts) => (
                <li key={date.toDateString() + ts}>{ts}</li>
              ))}
            </div>
          </div>
        );
      });
      return print;
    }
  };
  return (
    <>
      <Calendar
        defaultDate={defaultDate}
        components={components}
        localizer={localizer}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 600 }}
        views={views}
        events={eventList}
        formats={formats}
        // step={60}
        eventPropGetter={eventPropGetter}
        onSelectEvent={handleSelectEvent}
        min={min}
        max={max}
        defaultView={Views.DAY}
        dayLayoutAlgorithm="no-overlap"
        popup={true}
        selectable={false}
      />
      <div style={{ marginTop: 5, color: '#787878' }}>
        *Select min {props.minimumSlots} time{' '}
        {props.minimumSlots > 1 ? 'slots' : 'slot'}
      </div>
      <div>{showSelectedEvents()}</div>
    </>
  );
};

export const CALENDAR_PIPE = '|';

const getMinDate = (defaultValue: any) => {
  const dateList = [];
  for (const [key, _] of Object.entries(defaultValue)) {
    dateList.push(new Date(key));
  }
  if (!dateList || dateList.length === 0) {
    dateList.push(new Date());
  }

  // the earlies date will be the first date in the list
  const result = dateList.sort(compareAsc)[0];
  return result;
};
// defaultValue - { "Mon Aug 22 2022": [{id: 5, name: "8:00 AM - 12:00 PM", value: "08:00" }]}
const getEvents = (defaultValue: any) => {
  let date;
  // if there is a default value then we need to get the min date when user selected the slot
  // start the Today date to the minimum date and take the user to that date initialy
  if (defaultValue) {
    date = getMinDate(defaultValue);
  }
  if (!date || date > new Date()) {
    date = utcToZonedTime(new Date(), 'Europe/London');
  }
  date.setHours(date.getHours() + MIN_HOURS);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day_of_month = date.getDate();

  let events: any[] = [];
  for (let i = 0; i < 60; i++) {
    const todayDate = new Date(year, month, day_of_month + i, date.getHours());
    const today = todayDate.getDay();
    const isWeekend = today === 0 || today === 6;
    let text = '';
    if (isWeekend) {
      text = ' - OOH’s 1.5x rates apply';
    }
    // slots which are already selected by admin or user
    let selectedSlots = [];
    if (defaultValue) {
      selectedSlots = defaultValue[todayDate.toDateString()];
    }

    if (!isWeekend) {
      // making sure that the first day - hours are not less than 8
      if (i !== 0 || todayDate.getHours() < 8) {
        // Need to make changes in the PaymentStatus if any of the details are changed
        events.push(
          addEvent(
            i,
            5,
            `8 AM - 12 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 8, 0, 0),
            new Date(year, month, day_of_month + i, 12, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 9) {
        events.push(
          addEvent(
            i,
            7,
            `9 AM - 1 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 9, 0, 0),
            new Date(year, month, day_of_month + i, 13, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 10) {
        events.push(
          addEvent(
            i,
            9,
            `10 AM to 2 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 10, 0, 0),
            new Date(year, month, day_of_month + i, 14, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 11) {
        events.push(
          addEvent(
            i,
            11,
            `11 AM to 3 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 11, 0, 0),
            new Date(year, month, day_of_month + i, 15, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 12) {
        events.push(
          addEvent(
            i,
            13,
            `12 PM to 4 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 12, 0, 0),
            new Date(year, month, day_of_month + i, 16, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 13) {
        events.push(
          addEvent(
            i,
            15,
            `1 PM to 5 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 13, 0, 0),
            new Date(year, month, day_of_month + i, 17, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 14) {
        events.push(
          addEvent(
            i,
            17,
            `2 PM to 6 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 14, 0, 0),
            new Date(year, month, day_of_month + i, 18, 0, 0),
            selectedSlots,
          ),
        );
      }
      if (i !== 0 || todayDate.getHours() < 15) {
        events.push(
          addEvent(
            i,
            19,
            `3 PM to 7 PM ${text}`,
            new Date(year, month, day_of_month + i),
            new Date(year, month, day_of_month + i, 15, 0, 0),
            new Date(year, month, day_of_month + i, 19, 0, 0),
            selectedSlots,
          ),
        );
      }
    }
  }
  return events;
};

const addEvent = (
  index: number,
  id: number,
  title: string,
  date: Date,
  start: Date,
  end: Date,
  selectedSlots: TimeSlotProps[],
) => {
  let findIndex = -1;

  // check if the slot is already selected by admin or user
  if (selectedSlots && selectedSlots.length > 0) {
    findIndex = selectedSlots?.findIndex((slot) => slot.id === id);
  }
  return {
    id: `${index}${CALENDAR_PIPE}${id}`,
    title,
    date,
    start,
    end,
    selected: findIndex === -1 ? false : true,
  };
};

export default CalendarComponent;
